import { Component, inject, HostListener } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Subscription, map, Observable, take, skip } from 'rxjs';
import { ErrorService } from '../shared/error.service';
import { commonImports } from '../shared/table-interface';
import { UtilityService } from '../shared/utility.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { AsyncPipe, CommonModule } from '@angular/common';
import { CodeableConcept2Pipe } from '../shared/codeable-concept2.pipe';
import { Bundle, BundleEntry, ChargeItemDefinition, FhirResource, Medication, MedicationRequest, Resource } from 'fhir/r4';
import { CodeableRef2Pipe } from '../shared/codeable-ref2.pipe';
import { References2Pipe } from '../shared/references2.pipe';
import { fetchFromReferencePipe } from '../shared/Fetch.pipe';
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
import { MatTooltipModule } from '@angular/material/tooltip';
import { baseStatusStyles } from '../shared/statusUIIcons';
import { DetailBaseComponent } from '../shared/detail-base/detail-base.component';
import { DummyMedicationRequestDetailsComponent } from '../dummy-medication-request-details/dummy-medication-request-details.component';
import { DynamicFormsV2Component } from '../shared/dynamic-forms-v2/dynamic-forms-v2.component';
import { formMetaData, generalFieldsData } from '../shared/dynamic-forms.interface2';
import { StatusChangesService } from '../status-changes.service';

import {
  CodeableConceptField, CodeField, GroupField, IndividualField,
  codeableConceptDataType, ReferenceDataType, codingDataType,
  IndividualReferenceField, ReferenceFieldArray,
  SingleCodeField
} from '../shared/dynamic-forms.interface2';
import { AuthService, capacityObject } from '../shared/auth/auth.service';
import { EncounterServiceService } from '../patient-wrapper/encounter-service.service';
import { AddMedicationComponent } from './add-medication/add-medication.component';
import { PriceFormatPipe } from "../shared/price-format.pipe";
import { StateService } from '../shared/state.service';
// import { Det } from '../detailz-viewz/detailz-viewz.service';
import { ChipsDirective } from '../chips.directive';
import { DetailActionButton, DetailzViewzComponent } from '../detailz-viewz/detailz-viewz.component';
import { DetailsBuilderObject } from '../detailz-viewz/details-builder.service';
import { NaPipe } from '../shared/na.pipe';
import { EmptyStateComponent } from "../shared/empty-state/empty-state.component";
import { TableHeaderComponent } from '../table-header/table-header.component';
import { EmphasisDirective } from '../shared/directives/emphasis.directive';
import { backendEndPointToken, terminologyCanonicalUrlBaseToken } from '../app.config';
import { LoadingUIEnabled, DisableCountParam } from '../loading.interceptor';
import { ResourceRefreshService } from '../shared/resource-refresh.service';
import { MedicationStockAdjustDialogComponent } from './medication-stock-adjust-dialog/medication-stock-adjust-dialog.component';
import { TableFilteringService, TableFilters, TableRowModel } from '../shared/table-filtering.service';
import { MedicationImportComponent } from './medication-import/medication-import.component';


type FormFields = IndividualField | ReferenceFieldArray | CodeableConceptField | CodeField | IndividualReferenceField | GroupField;

type MedicationRow = Medication & TableRowModel & {
  inStockLabel: string;
  expiredLabel: string | null;
  remainingValue?: number | null;
  remainingUnit?: string | null;
  lowStock?: boolean;
  lowStockLabel?: string | null;
};

@Component({
  selector: 'app-medication2',
  imports: [
    ...commonImports,
    CommonModule,
    EmphasisDirective,
    ChipsDirective,
    NaPipe,
    CodeableConcept2Pipe,
    MatProgressSpinnerModule,

    EmptyStateComponent,
    TableHeaderComponent,
    MatTooltipModule,
    MatPaginatorModule,
    MatSnackBarModule,
    MedicationImportComponent
  ],
  templateUrl: './medication2.component.html',
  styleUrls: ['../medicine-requests/medicine-requests.component.scss', './medication2.component.scss']
})
export class MedicationComponent2 {
  medicationTableFilterArray = new Map<string, string[]>([
    ['status', ['active', 'inactive', 'entered-in-error', 'retired']],
    ['form', ['tablet', 'capsule', 'syrup', 'injection', 'ointment']]
  ]);
  medicationFiltersFormControlObject: Record<string, FormGroup> = {};
  private fb = inject(FormBuilder);
  dialog = inject(MatDialog);
  auth = inject(AuthService);
  capacityObject = capacityObject;
  stateService = inject(StateService);
  utilityService = inject(UtilityService);
  encounterService = inject(EncounterServiceService);
  errorService = inject(ErrorService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private tableFiltering = inject<TableFilteringService<Medication, MedicationRow>>(TableFilteringService);
  private resourceRefresh = inject(ResourceRefreshService);
  private readonly userId = this.auth.user?.getValue()?.['fhirResourceId'];
  private readonly silentHttpContext = new HttpContext().set(LoadingUIEnabled, false).set(DisableCountParam, true);
  private snackBar = inject(MatSnackBar);
  private terminologyServer = inject(terminologyCanonicalUrlBaseToken);
  private readonly subscriptions = new Subscription();

  tableDataSource = new MatTableDataSource<MedicationRow>();
  private readonly tableData$ = this.tableDataSource.connect();
  readonly loading$ = this.tableFiltering.loading$;
  readonly total$ = this.tableFiltering.total$;
  readonly unknownTotal$ = this.tableFiltering.unknownTotal$;
  readonly error$ = this.tableFiltering.error$;
  readonly pageSize = 25;
  readonly maxCacheRows = 200;
  private readonly inventoryNumberFormatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });

  private activeFilters: Record<string, string[]> = {};
  private readonly commaOrFilters = new Set<string>();
  searchTerm = '';
  private tableFilteringReady = false;
  private skipNextRouteQueryParamsEmit = false;
  hasQueryParams = false;
  paginatorPageIndex = 0;
  medicationDisplayedColumns = ['code', 'status', 'manufacturer', 'amount', 'in-stock', 'action'];
  private desktopColumns: string[] = ['code', 'status', 'manufacturer', 'amount', 'in-stock', 'action'];
  private mobileColumns: string[] = ['code', 'status', 'in-stock', 'action'];
  patientId: string | null = null;
  useOrgWideMedications = false;
  hasLoadedAnyRows = false;
  medicationSearchFieldOptions: { label: string; value: string }[] = [
    { label: 'Medication Name', value: 'code:text' },
    { label: 'Ingredient', value: 'ingredient-code:text' }
  ];
  selectedSearchField = this.medicationSearchFieldOptions[0].value;

  canAddMedication$ = this.auth.user.pipe(map(user => this.auth.can('medication', 'add')));
  canExportMedication$ = this.auth.user.pipe(map(user => this.auth.can('medication', 'viewAll')));

  detailsBuilder: DetailsBuilderObject = {
    resourceName: 'Medication',
    resourceIcon: 'medical_services',
    specialHeader: {
      strongSectionKey: 'code',
      iconSectionKeys: ['status', 'price'],
      contentSectionKeys: ['manufacturer', 'form']
    },
    groups: [
      { groupName: 'Identification', groupIcon: 'pill', groupKeys: ['code', 'manufacturer', 'status'] },
      { groupName: 'Packaging', groupIcon: 'inventory_2', groupKeys: ['form', 'amount'] },
      //inventory group
      { groupName: 'Inventory', groupIcon: 'inventory_2', groupKeys: ['inStock', 'expiry Date'] },
      { groupName: 'Pricing', groupIcon: 'attach_money', groupKeys: ['price'] },
      {
        groupName: 'Ingredients', groupIcon: 'science', groupKeys: ['ingredient',
          'ingredient[0].itemCodeableConcept', 'ingredient[0].isActive', 'ingredient[0].strength',
          'ingredient[1].itemCodeableConcept', 'ingredient[1].isActive', 'ingredient[1].strength',
          'ingredient[2].itemCodeableConcept', 'ingredient[2].isActive', 'ingredient[2].strength',
          'ingredient[3].itemCodeableConcept', 'ingredient[3].isActive', 'ingredient[3].strength'
        ]
      }
    ]
  };

  ngOnInit() {
    console.log('MedicationComponent2 ngOnInit start', { });
    this.utilityService.fetchAll<ChargeItemDefinition>({
      resourceType: 'ChargeItemDefinition',
      queries: { status: 'active' },
      checkOrgWideState: true,
      updateOrgWideState: true
    }).subscribe((cids: ChargeItemDefinition[]) => {
      cids.forEach((cid: ChargeItemDefinition) => {
        if (cid.instance && cid.instance?.[0]?.reference
          && cid.instance?.[0]?.reference.startsWith('Medication/')
        ) {
          const medId = cid.instance[0].reference.replace('Medication/', '');
          if (!this.chargeItemDefinitions.has(medId)) {
            this.chargeItemDefinitions.set(medId, cid);
          }
        }
      });
    });
    this.medicationTableFilterArray.forEach((values, key) => {
      const group = this.fb.group({});
      values.forEach(value => group.addControl(value, new FormControl(false)));
      this.medicationFiltersFormControlObject[key] = group;
    });
    this.patientId = this.utilityService.getPatientIdFromRoute();
    const resolvedPatientId = this.stateService.currentPatientIdFromResolver.getValue();
    const encounterPatientId = this.stateService.currentEncounter.getValue()?.patientId ?? null;

    if (!resolvedPatientId && !encounterPatientId) {
      if (!this.auth.can('medication', 'viewAll')) {
        this.errorService.openandCloseError('No patient selected. Please pick a patient to view medications.');
        return;
      }
      this.useOrgWideMedications = true;
    } else {
      this.patientId = resolvedPatientId ?? encounterPatientId;
    }

    // If the initial navigation included query params, prefer them as the initial
    // query so the service doesn't first fetch the component default and then
    // immediately get cancelled by the deep-link search. We use the route
    // snapshot to synchronously detect params and build the initial query.
    const snapshotParams = this.route.snapshot.queryParams || {};
    this.hasQueryParams = Object.keys(snapshotParams).length > 0;
    if (this.hasQueryParams) {
      const base = this.buildDefaultQuery().split('?')[0];
      const searchParams = new URLSearchParams();
      for (const k of Object.keys(snapshotParams)) {
        const v = (snapshotParams as any)[k];
        if (Array.isArray(v)) {
          v.forEach((item) => searchParams.append(k, String(item)));
        } else {
          searchParams.set(k, String(v));
        }
      }
      const initialQuery = `${base}?${searchParams.toString()}`;
      this.skipNextRouteQueryParamsEmit = true;
      this.initializeTableFiltering(initialQuery);
      console.log('MedicationComponent2: initializeTableFiltering() called with initialQuery from snapshot; tableFilteringReady=', this.tableFilteringReady, 'initialQuery=', initialQuery);
    } else {
      this.initializeTableFiltering();
      console.log('MedicationComponent2: initializeTableFiltering() called; tableFilteringReady=', this.tableFilteringReady);
    }

    // If the route contains query params, use them to run an immediate search so
    // deep-links / navigations to this page can show a specific medication.
    this.subscriptions.add(
      this.route.queryParams.subscribe((params) => {
        try {
          console.log('MedicationComponent2: route.queryParams', params, 'tableFilteringReady=', this.tableFilteringReady, 'skipNext=', this.skipNextRouteQueryParamsEmit);
          if (this.skipNextRouteQueryParamsEmit) {
            // We already applied the snapshot params as the initial query — ignore
            // the first emitted value from the observable which mirrors the snapshot.
            this.skipNextRouteQueryParamsEmit = false;
            return;
          }
          const keys = Object.keys(params || {});
          this.hasQueryParams = keys.length > 0;
          if (!keys.length) {
            // no query params — nothing to search
            return;
          }
          const filters: TableFilters = {};
          for (const k of keys) {
            // Angular queryParams values are strings; when a param appears multiple times
            // it may be an array — pass through as-is.
            filters[k] = params[k] as any;
          }
          console.log('MedicationComponent2: calling tableFiltering.search with', filters);
          // Kick off the table search using the provided params
          this.tableFiltering.search(filters);
        } catch (e) {
          console.error('MedicationComponent2: Failed to process route.queryParams', e);
        }
      })
    );

    this.subscriptions.add(
      this.tableData$.subscribe(rows => {
        console.log('MedicationComponent2: tableData$ emitted rows count', Array.isArray(rows) ? rows.length : 0);
        if (Array.isArray(rows) && rows.length > 0) {
          this.hasLoadedAnyRows = true;
        }
      })
    );

    // responsive columns
    this.updateDisplayedColumns();

    // subscribe to refresh signals
    this.resourceRefresh.onRefresh('medication-inventory').subscribe((opts) => {
      try {
        if (opts?.full) {
          this.initializeTableFiltering();
        } else {
          this.tableFiltering.refreshCurrentPage();
        }
      } catch (e) { }
    });

    this.subscriptions.add(
      this.stateService.orgWideResources.medications.pipe(skip(1)).subscribe(() => {
        try {
          this.tableFiltering.refreshCurrentPage();
        } catch (e) { }
      })
    );
  }

  // Clear any query-param based filtering: reset table and remove params from URL
  public clearSearch(): void {
    try {
      // reset active filters and search term
      this.activeFilters = {};
      this.searchTerm = '';
      // reset header form controls
      try {
        Object.values(this.medicationFiltersFormControlObject).forEach((group) => {
          Object.keys((group.controls || {})).forEach((k) => group.controls[k].setValue(false));
        });
      } catch (e) {
        // ignore any UI reset failures
      }

      // Reinitialize the table filtering with the component default query (no deep-link params)
      this.initializeTableFiltering();
      this.hasQueryParams = false;

      // remove query params from URL without adding history
      this.router.navigate([], { relativeTo: this.route, queryParams: {}, replaceUrl: true });
    } catch (e) {
      console.error('MedicationComponent2.clearSearch failed', e);
    }
  }

  ngOnDestroy(): void {
    this.tableFiltering.destroy();
    this.tableDataSource.disconnect();
    this.subscriptions.unsubscribe();
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.updateDisplayedColumns();
  }

  private updateDisplayedColumns(): void {
    try {
      const isMobile = window.innerWidth < 600;
      this.medicationDisplayedColumns = isMobile ? [...this.mobileColumns] : [...this.desktopColumns];
    } catch (e) {
      this.medicationDisplayedColumns = [...this.desktopColumns];
    }
  }

  onFilterChanged(event: { filterKey: string; values: string[] }): void {
    this.activeFilters[event.filterKey] = event.values;
    this.emitFilters();
  }

  onSearchCriteriaChanged(criteria: { term: string; field: string | null }): void {
    this.searchTerm = this.normalizeSearchTerm(criteria?.term);
    const fallbackField = this.medicationSearchFieldOptions[0]?.value ?? 'code:text';
    this.selectedSearchField = criteria?.field ?? this.selectedSearchField ?? fallbackField;
    this.emitFilters();
  }

  onPageChange(event: PageEvent): void {
    this.paginatorPageIndex = event.pageIndex;
    this.tableFiltering.changePage(event.pageIndex);
  }

  private initializeTableFiltering(initialQuery?: string): void {
    const defaultQuery = initialQuery ?? this.buildDefaultQuery();
    console.log('MedicationComponent2.initializeTableFiltering defaultQuery=', defaultQuery);
    this.tableFiltering.init({
      resourceType: 'Medication',
      defaultQuery,
      dataSource: this.tableDataSource,
      baseUrl: this.backendEndPoint,
      pageSize: this.pageSize,
      maxCacheRows: this.maxCacheRows,
      transformEntry: (resource, entry) => this.transformMedication(resource, entry),
      httpOptions: {
        context: this.silentHttpContext
      }
    });
    this.tableFilteringReady = true;
    console.log('MedicationComponent2.tableFilteringReady = true');
  }

  private buildDefaultQuery(): string {
    if (this.useOrgWideMedications || !this.patientId) {
      return '/Medication';
    }
    return `/Medication?subject=Patient/${this.patientId}`;
  }

  private transformMedication(resource: Medication, entry?: BundleEntry<Medication>): MedicationRow {
    const fallbackId = resource.id || entry?.resource?.id || entry?.fullUrl || `Medication-${Date.now()}`;
    const expired = resource.batch?.expirationDate
      ? new Date(resource.batch.expirationDate) < new Date()
      : false;
    const stockInfo = this.utilityService.getMedicationTotalRemaining(resource);
    const remainingValue = stockInfo?.value ?? null;
    const remainingUnit = stockInfo?.unit ?? null;
    const lowStockConfig = this.utilityService.getMedicationLowStockConfig(resource);
    const lowStock = this.utilityService.isBelowLowStockThreshold(remainingValue, lowStockConfig?.threshold);

    return {
      ...resource,
      id: fallbackId,
      resourceType: resource.resourceType ?? 'Medication',
      display: resource.code?.text ?? resource.id ?? fallbackId,
      rawResource: resource,
      inStockLabel: this.formatRemainingLabel(remainingValue, remainingUnit),
      expiredLabel: expired ? 'expired' : null,
      remainingValue,
      remainingUnit,
      lowStock,
      lowStockLabel: lowStock ? this.buildLowStockLabel(remainingValue, remainingUnit, lowStockConfig) : null
    };
  }

  private emitFilters(): void {
    if (!this.tableFilteringReady) {
      return;
    }
    const payload = this.buildFilterPayload();
    // console.log('MedicationComponent2.emitFilters payload=', payload);
    this.tableFiltering.search(payload);
    this.paginatorPageIndex = 0;
  }

  private buildFilterPayload(): TableFilters {
    const filters: TableFilters = {};
    Object.entries(this.activeFilters).forEach(([key, values]) => {
      if (Array.isArray(values) && values.length > 0) {
        filters[key] = values.join(',');
      }
    });
    if (this.searchTerm) {
      const searchKey = this.selectedSearchField || this.medicationSearchFieldOptions[0]?.value || 'code:text';
      filters[searchKey] = this.searchTerm;
    }
    return filters;
  }

  private normalizeSearchTerm(term?: string | null): string {
    return term ? term.trim().toLowerCase() : '';
  }

  http = inject(HttpClient);
  backendEndPoint = inject(backendEndPointToken);
  chargeItemDefinitions: Map<string, ChargeItemDefinition> = new Map();

  showRow(row: MedicationRow) {
// $#$    // /* alert(this.seperateTotalPriceComponents(this.chargeItemDefinitions.get(row.id || '') */ as ChargeItemDefinition));
    let price = ""
    if (this.chargeItemDefinitions.get(row.id || '')) {
      const result = this.utilityService.seperateTotalPriceComponents(this.chargeItemDefinitions.get(row.id || '') as ChargeItemDefinition);
      price = new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: result?.currency || 'NGN',

      }).format(result?.totalSellingPrice || 0);
    } else {
      price = 'N/A'
    }
    const isOutOfStock = (row.inStockLabel || this.getRemainingInventory(row)) === 'out of stock';
    if (isOutOfStock && !this.detailsBuilder.specialHeader.iconSectionKeys.includes('out-of-stock')) {
      this.detailsBuilder.specialHeader.iconSectionKeys.push('out-of-stock');
    }
    if (isOutOfStock) {
      row['out-of-stock'] = 'out of stock';
    }
    if (row.lowStock && !this.detailsBuilder.specialHeader.iconSectionKeys.includes('low-stock')) {
      this.detailsBuilder.specialHeader.iconSectionKeys.push('low-stock');
    }
    if (row.lowStock) {
      row['low-stock'] = row.lowStockLabel || 'low stock';
    }
    //als fr expiry
    if (row.batch?.expirationDate && new Date(row.batch.expirationDate) < new Date() && !this.detailsBuilder.specialHeader.iconSectionKeys.includes('expired')) {
      this.detailsBuilder.specialHeader.iconSectionKeys.push('expired');
      row['expired'] = 'expired';
    }

    const expiryDate = row.batch?.expirationDate ? new Date(row.batch.expirationDate).toLocaleDateString() : 'N/A';
    const inStock = row.inStockLabel || this.getRemainingInventory(row) || 'N/A';

    //  ?code=${encodeURIComponent(row.code?.coding?.[0]?.code || '')}`).subscribe((bundleResp) => {
    const dref = this.dialog.open(DetailzViewzComponent, {

      maxHeight: '93vh',
      maxWidth: '90vh',
      data: {
        resourceData: { ...row, price, inStock, 'expiry Date': expiryDate },
        detailsBuilderObject: this.detailsBuilder,
        actionButtons: <DetailActionButton[]>[
          {
            key: 'changeStatus',
            label: 'Change Status',
            icon: 'swap_horiz',
            capabilities: [{ resource: 'medication', action: 'edit' }]
          },
          {
            key: 'adjustStock',
            label: 'Adjust Stock',
            icon: 'inventory_2',
            capabilities: [{ resource: 'medication', action: 'stock' }]
          }
        ]

      }
    });


    dref.componentInstance.actionInvoked.subscribe(({ key }) => {
      if (key === 'changeStatus') {
        this.openStatusChangeDialog(row);
        dref.close();
      } else if (key === 'adjustStock') {
        this.openStockAdjustmentDialog(row);
        dref.close();
      }
    });
  }

  private openStatusChangeDialog(medication: any): void {
    const currentStatus = medication.status || '';
    const availableStatuses = this.statusChangesService.bigStatusOptionsByResources['Medication'] ||
      ['active', 'inactive', 'entered-in-error'];

    const dynF = this.dialog.open(DynamicFormsV2Component, {
      width: '400px',
      data: {
        formMetaData: <formMetaData>{
          formName: 'Change Medication Status',
          closeDialogOnSubmit: true,
          formDescription: `Current status: ${currentStatus}`,
          submitText: 'Update Status'
        },
        formFields: [
          <SingleCodeField>{
            generalProperties: <generalFieldsData>{
              fieldApiName: 'status',
              fieldLabel: 'Status',
              fieldName: 'Status',
              fieldType: 'SingleCodeField',
              inputType: 'select',
              isArray: false,
              isGroup: false,
              value: currentStatus,
              validations: [{
                type: 'default',
                isFunction: false,
                name: 'required'
              }],
              auth: {
                read: 'all',
                write: 'pharmacist, admin'
              }
            },
            data: availableStatuses,
            dataType: 'string'
          }
        ]
      }
    });

    dynF.afterClosed().subscribe((formResult) => {
      if (formResult?.values?.status && formResult.values.status !== currentStatus) {
        if (formResult.values.status === 'entered-in-error') {
          const changesExts = (medication.extension || []).filter((e: any) => e.url?.endsWith('/totalRemainingChanges'));
          if (changesExts.length > 0) {
            this.errorService.openandCloseError('Cannot mark as entered-in-error: stock movements have been recorded. Please use Stock Adjustment to change the stock to 0 instead.');
            return;
          }
        }
        
        this.statusChangesService.changeStatus(
          'Medication',
          'org',
          medication.id || '',
          formResult.values.status
        );
      }
    });

  }




  private openStockAdjustmentDialog(medication: Medication | MedicationRow): void {
    if (!medication?.id) {
      this.errorService.openandCloseError('Cannot adjust stock: missing medication id');
      return;
    }

    const resourceForDialog = this.cloneMedicationResource(medication);

    const ref = this.dialog.open(MedicationStockAdjustDialogComponent, {
      width: '520px',
      maxWidth: '95vw',
      data: { resource: resourceForDialog }
    });

    ref.afterClosed().pipe(take(1)).subscribe((updated: Medication | undefined) => {
      if (!updated?.id) return;
      this.upsertMedicationInState(updated);
      this.tableFiltering.refreshCurrentPage();
    });
  }

  private cloneMedicationResource(resource: Medication | MedicationRow): Medication {
    const base = (resource as MedicationRow)?.rawResource ?? resource;
    return JSON.parse(JSON.stringify(base)) as Medication;
  }

  private upsertMedicationInState(resource: Medication): void {
    const id = (resource?.id ?? '').toString().trim();
    if (!id) return;

    const wrapper = {
      referenceId: `Medication/${id}`,
      savedStatus: 'saved' as const,
      actualResource: resource
    };

    const updateList = (
      subject?: BehaviorSubject<Array<{ referenceId: string | null; savedStatus: 'saved' | 'unsaved'; actualResource: Medication }>>
    ): void => {
      if (!subject) return;
      const current = subject.getValue() || [];
      const idx = current.findIndex((entry) => (entry?.actualResource?.id || '') === id || (entry?.referenceId || '') === `Medication/${id}`);
      const next = [...current];
      if (idx >= 0) {
        next[idx] = { ...wrapper };
      } else {
        next.unshift({ ...wrapper });
      }
      subject.next(next);
    };

    updateList(this.stateService.orgWideResources?.medications);
    updateList(this.stateService.PatientResources?.medications);
  }

  importMedications() {
    this.dialog.open(MedicationImportComponent, {
      maxHeight: '93vh',
      maxWidth: '650px',
      autoFocus: false,
      disableClose: true,
    });
  }

  addMedication() {
    this.dialog.open(AddMedicationComponent, {
      maxHeight: '93vh',
      maxWidth: '650px',
      autoFocus: false,
      disableClose: true,
    });
  }

  getRemainingInventory(med: Medication): string {
    const stockInfo = this.utilityService.getMedicationTotalRemaining(med);
    const remainingValue = stockInfo?.value ?? null;
    const unit = stockInfo?.unit ?? undefined;
    return this.formatRemainingLabel(remainingValue, unit);
  }

  private findInventoryExtension(med: Medication): any | undefined {
    return med.extension?.find((ext: any) =>
      typeof ext.url === 'string'
      && ext.url.includes('totalRemaining')
      //&& ext.url.includes('medication-inventory-details')
    );
  }

  private extensionFieldKey(ext: { url?: string }): string {
    const url = ext?.url || '';
    return url.split('/').pop() || url;
  }

  private formatRemainingLabel(value?: number | null, unit?: string | null): string {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return 'N/A';
    }
    if (value <= 0) {
      return 'out of stock';
    }
    const formatted = this.inventoryNumberFormatter.format(value);
    return unit ? `${formatted} ${unit}` : formatted;
  }

  private buildLowStockLabel(
    remaining: number | null | undefined,
    unit: string | null | undefined,
    config: { threshold?: number; unit?: string } | null | undefined
  ): string {
    const normalizedRemaining = typeof remaining === 'number' && Number.isFinite(remaining)
      ? Math.max(0, remaining)
      : null;
    const remainingText = normalizedRemaining === null
      ? 'Remaining unknown'
      : `${this.inventoryNumberFormatter.format(normalizedRemaining)}${unit ? ` ${unit}` : ''}`;

    if (config?.threshold !== undefined) {
      const thresholdText = `${this.inventoryNumberFormatter.format(config.threshold)}${(config.unit || unit) ? ` ${config.unit || unit}` : ''}`;
      // return `(low stock) (threshold ${thresholdText})`;
      //just low stock
      return  `low stock`;
    }

    return `${remainingText} (low stock)`;
  }
  private statusChangesService = inject(StatusChangesService);

  showRowi(row: any): void {
    const actionButtons: DetailActionButton[] = [
      {
        key: 'changeStatus',
        label: 'Change Status',
        icon: 'swap_horiz',
        capabilities: [{ resource: 'medication', action: 'edit' }]
      }
    ];

    const dref = this.dialog.open(DetailzViewzComponent, {
      width: '90vw',
      maxWidth: '1000px',
      data: {
        resourceData: row,
        excludeKeys: ['id', 'meta', 'text', 'contained'],
        showOnlyKeysWithValues: true,
        actionButtons: actionButtons
      }
    });

    dref.componentInstance.actionInvoked.subscribe(({ key }) => {
      if (key === 'changeStatus') {
        this.openStatusChangeDialog(row);
        dref.close();
      }
    });
  }

  private openStatusChangeDialogi(medication: any): void {
    const currentStatus = medication.status || 'active';
    const availableStatuses = this.statusChangesService.bigStatusOptionsByResources['Medication'] ||
      ['active', 'inactive', 'entered-in-error'];

    const dynF = this.dialog.open(DynamicFormsV2Component, {
      width: '400px',
      data: {
        formMetaData: <formMetaData>{
          formName: 'Change Medication Status',
          closeDialogOnSubmit: true,
          formDescription: `Current status: ${currentStatus}`,
          submitText: 'Update Status'
        },
        formFields: [
          <SingleCodeField>{
            generalProperties: <generalFieldsData>{
              fieldApiName: 'status',
              fieldLabel: 'Status',
              fieldName: 'Status',
              fieldType: 'SingleCodeField',
              inputType: 'select',
              isArray: false,
              isGroup: false,
              value: currentStatus,
              validations: [{
                type: 'default',
                isFunction: false,
                name: 'required'
              }],
              auth: {
                read: 'all',
                write: 'pharmacist, admin'
              }
            },
            data: availableStatuses,
            dataType: 'string'
          }
        ]
      }
    });

    dynF.afterClosed().subscribe((formResult) => {
      if (formResult?.values?.status && formResult.values.status !== currentStatus) {
        if (formResult.values.status === 'entered-in-error') {
          const changesExts = (medication.extension || []).filter((e: any) => e.url?.endsWith('/totalRemainingChanges'));
          if (changesExts.length > 0) {
            this.errorService.openandCloseError('Cannot mark as entered-in-error: stock movements have been recorded. Please use Stock Adjustment to change the stock to 0 instead.');
            return;
          }
        }
        
        this.statusChangesService.changeStatus(
          'Medication',
          'org',
          medication.id || '',
          formResult.values.status
        );
      }
    });
  }

  async exportMedications() {
    this.snackBar.open('Preparing export...', 'Close', { duration: 2000 });
    try {
      const firstValueFrom = (await import('rxjs')).firstValueFrom;
      
      let queryUrl = this.tableFiltering.getCurrentQueryUrl();
      if (!queryUrl) {
          queryUrl = `/Medication?_sort=-_lastUpdated`;
      }
      
      let queryStr = '';
      const qIndex = queryUrl.indexOf('?');
      if (qIndex !== -1) {
          queryStr = queryUrl.substring(qIndex + 1);
      }

      const medications = await firstValueFrom(this.utilityService.fetchAll<Medication>({
        resourceType: 'Medication',
        queries: queryStr,
        checkOrgWideState: false,
        updateOrgWideState: false
      })) || [];

      if (medications.length === 0) {
        this.snackBar.open('No medications to export.', 'Close', { duration: 3000 });
        return;
      }

        // Determine max number of ingredients across all medications
        let maxIngredients = 1;
        medications.forEach(med => {
          if (med.ingredient && med.ingredient.length > maxIngredients) {
            maxIngredients = med.ingredient.length;
          }
        });

        // Build dynamic headers
        const csvHeaders = [
          'id', 
          'name_text', 'name_code', 'name_display', 'name_system',
          'status', 
          'form_text', 'form_code', 'form_display', 'form_system',
          'manufacturer',
          'amountNumeratorValue', 'amountNumeratorUnit', 'amountDenominatorValue', 'amountDenominatorUnit',
        ];

        for (let i = 1; i <= maxIngredients; i++) {
          csvHeaders.push(
            `ingredient${i}_name_text`, `ingredient${i}_name_code`, `ingredient${i}_name_display`, `ingredient${i}_name_system`,
            `ingredient${i}_isActive`, 
            `ingredient${i}_strengthNumeratorValue`, `ingredient${i}_strengthNumeratorUnit`, 
            `ingredient${i}_strengthDenominatorValue`, `ingredient${i}_strengthDenominatorUnit`
          );
        }

        csvHeaders.push(
          'lotNumber', 'expirationDate',
          'packageType', 'dispensableUnits', 'totalRemaining', 'lowStockThreshold', 'lowStockAlertsEnabled',
          'currency', 'sellingPrice', 'costPrice'
        );

        const mapConcept = (prefix: string, concept: any, rowObj: any) => {
          rowObj[`${prefix}_text`] = concept?.text || concept?.coding?.[0]?.display || concept?.coding?.[0]?.code || '';
          rowObj[`${prefix}_code`] = concept?.coding?.[0]?.code || '';
          rowObj[`${prefix}_display`] = concept?.coding?.[0]?.display || '';
          rowObj[`${prefix}_system`] = concept?.coding?.[0]?.system || '';
        };

        const rows = medications.map(med => {
          const cid = this.chargeItemDefinitions.get(med.id!);
          const extUrl = this.terminologyServer.endsWith('/') ? this.terminologyServer : this.terminologyServer + '/';
          const structureDefUrl = extUrl + 'StructureDefinition/';

          const getExt = (url: string) => med.extension?.find(e => e.url === structureDefUrl + url || e.url?.endsWith(url));

          const sellingPrice = cid?.propertyGroup?.[0]?.priceComponent?.find(p => p.type === 'base')?.amount?.value || '';
          const currency = cid?.propertyGroup?.[0]?.priceComponent?.find(p => p.type === 'base')?.amount?.currency || 'NGN';

          const costExt = cid?.extension?.find(e => e.url === "http://hl7.org/fhir/StructureDefinition/ChargeItemDefinition-totalCost");
          const costPrice = costExt?.extension?.find(e => e.url === "amount")?.valueMoney?.value || '';

          const packageExt = getExt('packageType');
          const packageType = packageExt?.valueCodeableConcept?.text || packageExt?.valueCodeableConcept?.coding?.[0]?.display || '';

          const row: any = {
            id: med.id,
            status: med.status || '',
            manufacturer: med.manufacturer?.display || med.manufacturer?.reference || '',
            amountNumeratorValue: med.amount?.numerator?.value || '',
            amountNumeratorUnit: med.amount?.numerator?.unit || '',
            amountDenominatorValue: med.amount?.denominator?.value || '',
            amountDenominatorUnit: med.amount?.denominator?.unit || '',
            lotNumber: med.batch?.lotNumber || '',
            expirationDate: med.batch?.expirationDate || '',
            packageType: packageType,
            dispensableUnits: getExt('dispensableUnits')?.valueString || '',
            totalRemaining: getExt('totalRemaining')?.valueQuantity?.value || '',
            lowStockThreshold: getExt('lowStockThreshold')?.valueQuantity?.value || '',
            lowStockAlertsEnabled: getExt('lowStockAlertsEnabled')?.valueBoolean || '',
            currency: currency,
            sellingPrice: sellingPrice,
            costPrice: costPrice
          };

          mapConcept('name', med.code, row);
          mapConcept('form', med.form, row);

          for (let i = 1; i <= maxIngredients; i++) {
            const ing = med.ingredient?.[i - 1];
            if (ing) {
              mapConcept(`ingredient${i}_name`, ing.itemCodeableConcept, row);
              row[`ingredient${i}_isActive`] = ing.isActive ?? '';
              row[`ingredient${i}_strengthNumeratorValue`] = ing.strength?.numerator?.value ?? '';
              row[`ingredient${i}_strengthNumeratorUnit`] = ing.strength?.numerator?.unit || '';
              row[`ingredient${i}_strengthDenominatorValue`] = ing.strength?.denominator?.value ?? '';
              row[`ingredient${i}_strengthDenominatorUnit`] = ing.strength?.denominator?.unit || '';
            } else {
              mapConcept(`ingredient${i}_name`, null, row);
              row[`ingredient${i}_isActive`] = '';
              row[`ingredient${i}_strengthNumeratorValue`] = '';
              row[`ingredient${i}_strengthNumeratorUnit`] = '';
              row[`ingredient${i}_strengthDenominatorValue`] = '';
              row[`ingredient${i}_strengthDenominatorUnit`] = '';
            }
          }

          return csvHeaders.map(h => row[h] ?? '');
        });

        try {
          const ExcelJS = await import('exceljs');
          const FileSaver = await import('file-saver');
          const Workbook = ExcelJS.Workbook || (ExcelJS as any).default?.Workbook || (ExcelJS as any).default;
          const workbook = new Workbook();
          const sheet = workbook.addWorksheet('Medications');
          
          sheet.addRow(csvHeaders);
          const headerRow = sheet.getRow(1);
          headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
          headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F81BD' } };
          
          rows.forEach(r => sheet.addRow(r));
          
          sheet.columns.forEach((column: any, index: number) => {
            const headerName = csvHeaders[index];
            if (headerName && (headerName.endsWith('_code') || headerName.endsWith('_system') || headerName.endsWith('_display'))) {
              column.hidden = true;
            } else {
              column.width = 15;
            }
          });

          // Fetch Dictionary for NAFDAC dropdown
          let dictionary: any = null;
          try {
            const firstValueFrom = (await import('rxjs')).firstValueFrom;
            dictionary = await firstValueFrom(this.http.get(`${this.backendEndPoint}/ValueSet/nafdac-medications`));
          } catch (e) {
            console.warn('Could not fetch nafdac-medications dictionary', e);
          }

          // Dictionary Sheet (Hidden)
          const dictSheet = workbook.addWorksheet('Dictionary', { state: 'hidden' });
          dictSheet.addRow(['Display Text', 'Code', 'System']);
          let dictRowCount = 1;
          const conceptList = dictionary?.compose?.include?.[0]?.concept || [];
          const systemUrl = dictionary?.compose?.include?.[0]?.system || '';
          for (const concept of conceptList) {
            if (concept.display && concept.code) {
              dictSheet.addRow([concept.display, concept.code, systemUrl]);
              dictRowCount++;
            }
          }

          // Protect the sheet
          await sheet.protect('password123', {
            selectUnlockedCells: true,
            selectLockedCells: true,
            formatCells: true,
            formatColumns: true,
            formatRows: true,
            insertRows: true,
            insertColumns: false,
            deleteRows: true,
          });

          // Lock headers
          headerRow.eachCell((cell) => { cell.protection = { locked: true }; });
          
          const expColIndex = csvHeaders.indexOf('expirationDate') + 1;
          const statusColIndex = csvHeaders.indexOf('status') + 1;
          const nameTextColIndex = csvHeaders.indexOf('name_text') + 1;
          const nameCodeColIndex = csvHeaders.indexOf('name_code') + 1;
          const nameDisplayColIndex = csvHeaders.indexOf('name_display') + 1;
          const nameSystemColIndex = csvHeaders.indexOf('name_system') + 1;

          // Define columns that SHOULD BE EDITABLE
          const editableColumns = ['status', 'sellingPrice', 'costPrice', 'lotNumber', 'expirationDate', 'packageType', 'dispensableUnits', 'totalRemaining', 'lowStockThreshold', 'lowStockAlertsEnabled'];

          // In exports, if they want to add new rows, we apply validations up to row 1000
          const maxRows = Math.max(rows.length + 500, 1000);

          for(let i = 2; i <= maxRows; i++) {
            const row = sheet.getRow(i);
            const isExistingRow = i <= rows.length + 1;

            // Lock all cells by default for existing rows. Unlocked for new blank rows.
            for (let col = 1; col <= csvHeaders.length; col++) {
              const cell = sheet.getCell(i, col);
              const headerName = csvHeaders[col - 1];
              if (isExistingRow) {
                // If existing row, only unlock the explicitly editable columns
                cell.protection = { locked: !editableColumns.includes(headerName) };
              } else {
                // If it's a blank row at the bottom, they can edit anything EXCEPT the hidden vlookup columns
                const isHiddenCodeCol = !!(headerName && (headerName.endsWith('_code') || headerName.endsWith('_system') || headerName.endsWith('_display')));
                cell.protection = { locked: isHiddenCodeCol };
              }
            }

            const dateCell = sheet.getCell(i, expColIndex);
            dateCell.numFmt = 'yyyy-mm-dd';
            dateCell.dataValidation = {
              type: 'date',
              operator: 'greaterThan',
              showErrorMessage: true,
              allowBlank: true,
              formulae: [new Date('2000-01-01')],
              errorStyle: 'error',
              errorTitle: 'Invalid Date',
              error: 'Please enter a valid date (e.g. 2026-12-31)'
            };

            const statusCell = sheet.getCell(i, statusColIndex);
            statusCell.dataValidation = {
              type: 'list',
              allowBlank: true,
              formulae: ['"active,inactive,entered-in-error"']
            };

            // Name Terminology validation & VLOOKUP formulas
            if (dictRowCount > 1) {
              const textCell = sheet.getCell(i, nameTextColIndex);
              textCell.dataValidation = {
                type: 'list',
                allowBlank: true,
                formulae: [`'Dictionary'!$A$2:$A$${dictRowCount}`]
              };

              // Apply VLOOKUP formulas ONLY to new blank rows. Existing rows already have their code/display values!
              if (!isExistingRow) {
                const textRef = textCell.address;
                const codeCell = sheet.getCell(i, nameCodeColIndex);
                const displayCell = sheet.getCell(i, nameDisplayColIndex);
                const systemCell = sheet.getCell(i, nameSystemColIndex);

                codeCell.value = { formula: `IFERROR(VLOOKUP(${textRef}, Dictionary!$A$2:$C$${dictRowCount}, 2, FALSE), "")` };
                displayCell.value = { formula: `IFERROR(VLOOKUP(${textRef}, Dictionary!$A$2:$C$${dictRowCount}, 1, FALSE), "")` };
                systemCell.value = { formula: `IFERROR(VLOOKUP(${textRef}, Dictionary!$A$2:$C$${dictRowCount}, 3, FALSE), "")` };
              }
            }
          }
          
          const buffer = await workbook.xlsx.writeBuffer();
          const saveAs = FileSaver.saveAs || (FileSaver as any).default?.saveAs || (FileSaver as any).default;
          saveAs(new Blob([buffer]), `medications_export_${new Date().toISOString().split('T')[0]}.xlsx`);
          this.snackBar.open('Export downloaded successfully.', 'Close', { duration: 3000 });
        } catch(e) {
          console.error('Failed to generate Excel export', e);
          this.snackBar.open('Failed to generate Excel export.', 'Close', { duration: 3000 });
        }
    } catch (err) {
      console.error('Export failed', err);
      this.snackBar.open('Export failed. Please try again.', 'Close', { duration: 5000 });
    }
  }
}
